// Détection centralisée des navigateurs et plateformes

/**
 * Détecte si l'utilisateur est sur iOS (tous navigateurs)
 */
export const isIOS = (): boolean => {
  const userAgent = navigator.userAgent;
  // Détecter iOS sur tous les navigateurs (Safari, Chrome, Firefox, etc.)
  return /iPad|iPhone|iPod/.test(userAgent);
};

/**
 * Détecte si l'utilisateur est sur Android
 */
export const isAndroid = (): boolean => {
  const userAgent = navigator.userAgent;
  return /Android/.test(userAgent);
};

/**
 * Détecte si l'utilisateur est sur mobile
 */
export const isMobile = (): boolean => {
  const userAgent = navigator.userAgent;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    userAgent
  );
};

/**
 * Détecte si l'utilisateur est sur desktop
 */
export const isDesktop = (): boolean => {
  return !isMobile();
};

/**
 * Détecte si l'utilisateur est sur Chrome
 */
export const isChrome = (): boolean => {
  const userAgent = navigator.userAgent;
  return /Chrome/.test(userAgent) && !/Edge/.test(userAgent);
};

/**
 * Détecte si l'utilisateur est sur Firefox
 */
export const isFirefox = (): boolean => {
  const userAgent = navigator.userAgent;
  return /Firefox/.test(userAgent);
};

/**
 * Détecte si l'utilisateur est sur Edge
 */
export const isEdge = (): boolean => {
  const userAgent = navigator.userAgent;
  return /Edge/.test(userAgent);
};

/**
 * Détecte si l'utilisateur est sur Safari
 */
export const isSafari = (): boolean => {
  const userAgent = navigator.userAgent;
  return (
    /Safari/.test(userAgent) &&
    !/Chrome/.test(userAgent) &&
    !/Firefox/.test(userAgent) &&
    !/Edge/.test(userAgent)
  );
};

/**
 * Détecte si l'utilisateur est sur Safari macOS (hors iOS)
 */
export const isSafariMac = (): boolean => {
  const userAgent = navigator.userAgent;
  const isSafari =
    /Safari/.test(userAgent) &&
    !/Chrome/.test(userAgent) &&
    !/Edge/.test(userAgent) &&
    !/Firefox/.test(userAgent);
  const isMac = /Macintosh/.test(userAgent);
  return isSafari && isMac;
};

/**
 * Obtient le nom du navigateur
 */
export const getBrowserName = (): string => {
  if (isChrome()) return "Chrome";
  if (isFirefox()) return "Firefox";
  if (isEdge()) return "Edge";
  if (isSafari()) return "Safari";
  return "Inconnu";
};

/**
 * Obtient la plateforme
 */
export const getPlatform = (): string => {
  if (isIOS()) return "iOS";
  if (isAndroid()) return "Android";
  if (isMobile()) return "Mobile";
  return "Desktop";
};

/**
 * Vérifie si les notifications push sont supportées
 */
export const areNotificationsSupported = (): boolean => {
  // iOS ne supporte pas les notifications push web SAUF en mode PWA
  if (isIOS()) {
    // Vérifier si l'application est en mode PWA
    if (isPWAInstalled()) {
      console.log("🔔 iOS détecté en mode PWA - notifications supportées");
      return true;
    } else {
      console.log("🔔 iOS détecté - notifications non supportées (PWA requis)");
      return false;
    }
  }

  // Vérifier que l'API Notification est disponible
  if (!("Notification" in window)) {
    console.log("🔔 API Notification non supportée par ce navigateur");
    return false;
  }

  return true;
};

/**
 * Vérifie si les notifications push distantes sont supportées
 */
export const areRemoteNotificationsSupported = (): boolean => {
  // iOS ne supporte pas les notifications push distantes SAUF en mode PWA
  if (isIOS()) {
    // Vérifier si l'application est en mode PWA
    if (isPWAInstalled()) {
      console.log(
        "🔔 iOS détecté en mode PWA - notifications distantes supportées"
      );
      return true;
    } else {
      console.log(
        "🔔 iOS détecté - notifications distantes non supportées (PWA requis)"
      );
      return false;
    }
  }

  // Vérifier que l'API Notification est disponible
  if (!("Notification" in window)) {
    return false;
  }

  // Vérifier que les service workers sont supportés
  if (!("serviceWorker" in navigator)) {
    return false;
  }

  return true;
};

/**
 * Obtient les limitations du navigateur actuel
 */
export const getBrowserLimitations = (): string[] => {
  const limitations: string[] = [];

  if (isIOS()) {
    if (isPWAInstalled()) {
      limitations.push("iOS en mode PWA - notifications supportées");
    } else {
      limitations.push("iOS ne supporte pas les notifications push web");
      limitations.push(
        "Installez l'application sur l'écran d'accueil pour activer les notifications"
      );
    }
  } else if (!("Notification" in window)) {
    limitations.push("API Notification non supportée");
  } else if (!("serviceWorker" in navigator)) {
    limitations.push("Service Workers non supportés");
  }

  return limitations;
};

/**
 * Debug: Affiche les informations du navigateur dans la console
 */
export const debugBrowserInfo = (): void => {
  console.log("🔍 Informations du navigateur:");
  console.log("  User Agent:", navigator.userAgent);
  console.log("  Navigateur:", getBrowserName());
  console.log("  Plateforme:", getPlatform());
  console.log("  iOS:", isIOS());
  console.log("  Android:", isAndroid());
  console.log("  Mobile:", isMobile());
  console.log("  Desktop:", isDesktop());
  console.log("  Notifications supportées:", areNotificationsSupported());
};

/**
 * Vérifie si l'application est installée en mode PWA
 */
export const isPWAInstalled = (): boolean => {
  // Vérifier si l'application est en mode standalone (PWA)
  if (
    window.matchMedia &&
    window.matchMedia("(display-mode: standalone)").matches
  ) {
    return true;
  }

  // Vérifier si l'application est en mode fullscreen
  if (
    window.matchMedia &&
    window.matchMedia("(display-mode: fullscreen)").matches
  ) {
    return true;
  }

  // Vérifier si l'application est en mode minimal-ui
  if (
    window.matchMedia &&
    window.matchMedia("(display-mode: minimal-ui)").matches
  ) {
    return true;
  }

  // Vérifier si l'application est en mode window-controls-overlay
  if (
    window.matchMedia &&
    window.matchMedia("(display-mode: window-controls-overlay)").matches
  ) {
    return true;
  }

  return false;
};

/**
 * Vérifie si l'installation PWA est supportée
 */
export const isPWAInstallationSupported = (): boolean => {
  // Sur iOS, l'installation PWA est toujours supportée via Safari
  if (isIOS()) {
    return true;
  }

  // Sur Android, vérifier si l'API beforeinstallprompt est disponible
  if (isAndroid()) {
    return "onbeforeinstallprompt" in window;
  }

  // Sur desktop, vérifier si l'API beforeinstallprompt est disponible
  return "onbeforeinstallprompt" in window;
};

/**
 * Vérifie si l'utilisateur est sur Safari iOS
 */
export const isSafariIOS = (): boolean => {
  const userAgent = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(userAgent) &&
    /Safari/.test(userAgent) &&
    !/Chrome/.test(userAgent)
  );
};

/**
 * Vérifie si l'utilisateur est sur Chrome iOS
 */
export const isChromeIOS = (): boolean => {
  const userAgent = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(userAgent) && /CriOS/.test(userAgent);
};

/**
 * Obtient les instructions d'installation PWA selon le navigateur
 */
export const getPWAInstallInstructions = (): {
  title: string;
  steps: string[];
  browser: string;
} => {
  if (isSafariIOS()) {
    return {
      title: "Installer l'application sur iOS (Safari)",
      browser: "Safari iOS",
      steps: [
        "Appuyez sur le bouton de partage (carré avec flèche vers le haut) en bas de l'écran",
        "Faites défiler et appuyez sur « Sur l'écran d'accueil »",
        "Appuyez sur « Ajouter » en haut à droite",
        "L'application apparaîtra sur votre écran d'accueil",
        "Ouvrez l'application depuis l'écran d'accueil pour activer les notifications",
      ],
    };
  }

  if (isChromeIOS()) {
    return {
      title: "Installer l'application sur iOS (Chrome)",
      browser: "Chrome iOS",
      steps: [
        "Appuyez sur le bouton de partage (trois points) en haut à droite",
        "Appuyez sur « Ajouter à l'écran d'accueil »",
        "Appuyez sur « Ajouter »",
        "L'application apparaîtra sur votre écran d'accueil",
        "Ouvrez l'application depuis l'écran d'accueil pour activer les notifications",
      ],
    };
  }

  if (isAndroid()) {
    return {
      title: "Installer l'application sur Android",
      browser: "Android",
      steps: [
        "Appuyez sur le bouton d'installation qui apparaît en haut de l'écran",
        "Ou appuyez sur le menu (trois points) puis « Installer l'application »",
        "Confirmez l'installation",
        "L'application sera installée sur votre appareil",
      ],
    };
  }

  return {
    title: "Installer l'application sur ordinateur",
    browser: "Desktop",
    steps: [
      "Appuyez sur le bouton d'installation qui apparaît dans la barre d'adresse",
      "Ou appuyez sur le menu (trois points) puis « Installer l'application »",
      "Confirmez l'installation",
      "L'application sera installée sur votre ordinateur",
    ],
  };
};

/**
 * Vérifie si l'utilisateur est sur Samsung Internet
 */
export const isSamsungInternet = (): boolean => {
  const userAgent = navigator.userAgent;
  return /SamsungBrowser/.test(userAgent);
};

/**
 * Vérifie si l'utilisateur est sur Android 13+
 */
export const isAndroid13Plus = (): boolean => {
  const userAgent = navigator.userAgent;
  const androidMatch = userAgent.match(/Android\s+(\d+)/);
  if (androidMatch) {
    const androidVersion = parseInt(androidMatch[1], 10);
    return androidVersion >= 13;
  }
  return false;
};

/**
 * Obtient les informations détaillées du navigateur pour le debug
 */
export const getDetailedBrowserInfo = (): {
  userAgent: string;
  browser: string;
  platform: string;
  androidVersion?: number;
  isSamsung: boolean;
  isAndroid13Plus: boolean;
} => {
  const userAgent = navigator.userAgent;
  const androidMatch = userAgent.match(/Android\s+(\d+)/);
  const androidVersion = androidMatch
    ? parseInt(androidMatch[1], 10)
    : undefined;

  return {
    userAgent,
    browser: getBrowserName(),
    platform: getPlatform(),
    androidVersion,
    isSamsung: isSamsungInternet(),
    isAndroid13Plus: isAndroid13Plus(),
  };
};
